import { useRef, useState } from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router';
import { mutateConfig } from '../api/mutationConfig';
import { setAuthToken } from '../api';

export const Confirm = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [digits, setDigits] = useState(Array(6).fill(''));
  const inputs = useRef<Array<HTMLInputElement | null>>([]);
  const { mutate: verify, isPending } = mutateConfig()<{ token: string }>({
    url: '/v1/ops/auth',
  });
  const { mutate: resend, isPending: isResending } = mutateConfig()<void>({
    url: '/v1/ops/otp',
  });

  if (!state?.email) {
    return <Navigate to="/login" replace />;
  }

  const isComplete = digits.every((d) => d !== '');

  const handleChange = (value: string, index: number) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...digits];
    next[index] = value;
    setDigits(next);
    if (value && index < 5) inputs.current[index + 1]?.focus();
  }

  const handleKeyDown = (e: { key: string }, index: number) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputs.current[index - 1]?.focus();
    }
  }

  const handlePaste = (e: React.ClipboardEvent, index: number) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6 - index);
    if (!pasted) {
      return;
    }
    const next = [...digits];
    for (let i = 0; i < pasted.length; i++) {
      next[index + i] = pasted[i];
    }
    setDigits(next);
    const focusIndex = Math.min(index + pasted.length, 5);
    inputs.current[focusIndex]?.focus();
  }

  const handleSubmit = (e: { preventDefault(): void }) => {
    e.preventDefault();
    verify(
      { email: state.email, otp: digits.join('') },
      {
        onSuccess: ({ payload }) => {
          localStorage.setItem('token', payload.token);
          setAuthToken(payload.token);
          navigate('/home');
        },
      }
    );
  }

  return (
    <div className="flex-1 flex items-center justify-center">
      <div className="w-full max-w-sm p-8 bg-white rounded-2xl shadow-sm">
        <h1 className="text-2xl font-semibold text-gray-900 mb-2">
          Check your email
        </h1>
        <p className="text-sm text-gray-500 mb-6">
          We sent a code to <span className="text-gray-900">{state.email}</span>
        </p>
        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          <div className="flex gap-2 justify-between">
            {digits.map((digit, i) => (
              <input
                key={i}
                ref={(el) => { inputs.current[i] = el; }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(e.target.value, i)}
                onKeyDown={(e) => handleKeyDown(e, i)}
                onPaste={(e) => handlePaste(e, i)}
                className="w-11 h-12 text-center text-lg font-medium border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
              />
            ))}
          </div>
          <button
            type="submit"
            disabled={!isComplete || isPending}
            className="w-full py-2 px-4 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Confirm
          </button>
          <p className="text-center text-sm text-gray-500">
            Didn't receive a code?{' '}
            <button
              type="button"
              disabled={isResending}
              onClick={() => resend({ email: state.email })}
              className="text-gray-900 font-medium hover:underline disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {isResending ? 'Sending...' : 'Resend'}
            </button>
          </p>
        </form>
      </div>
    </div>
  );
}
